// Transcript projection — projects the authoritative, source-bounded message
// rows into the plain-text model input consumed by the conversation-summaries
// pipeline and the summary-eval harness. Registered in the ADR-088 prompt-read
// scanner: the projected payload is parsed through the shared
// `enrichedTurnSchema`, never trusted via the column's `$type<>()` annotation,
// so a drifted write shape surfaces as a counted malformed turn instead of
// flowing into a prompt unnoticed.

import { enrichedTurnSchema, type ContentBlock } from './schema';

/** The bounded plain-text projection of a stored transcript. */
export interface TranscriptProjection {
  /** Rendered `[index] role: text` lines, joined — the model input. */
  transcript: string;
  /** Turns that survived projection (post-parse, post-truncation). */
  turnCount: number;
  /** Stored rows that failed the shared schema parse (structural drift signal). */
  malformedTurnCount: number;
  /** Turns dropped by the byte cap, plus one when a turn was shortened in place. */
  truncatedTurnCount: number;
}

/**
 * Input ceiling for the summarizer prompt. An unbounded transcript exceeds the
 * model context deterministically, which makes it a poison message: every DLQ
 * redrive re-fails identically, and the per-conversation token cost has no
 * upper bound. Matches the ceiling both sibling consumers apply to the same
 * stored payload.
 */
export const MAX_TRANSCRIPT_BYTES = 120_000;

/**
 * Project stored turns to the model input, PRESERVING ORIGINAL INDEXES.
 *
 * Each rendered line is prefixed `[i]` with the turn's index in the stored
 * transcript array — the coordinate system the model's citations use and the
 * grounding verify resolves against the same ordered message projection. Truncation
 * keeps the MOST RECENT turns and never renumbers survivors: dropping the
 * prefix's stability would break every citation downstream.
 *
 * Parsing is fail-open PER TURN (a bad turn degrades the input, counted, never
 * the whole conversation); only `text` blocks contribute — tool calls and tool
 * results are not dialogue. A single turn larger than the whole ceiling is
 * shortened in place at a code-point boundary rather than dropped or forwarded
 * whole.
 */
export function projectTranscript(
  storedTurns: unknown,
  maxBytes: number = MAX_TRANSCRIPT_BYTES,
): TranscriptProjection {
  const raw = Array.isArray(storedTurns) ? storedTurns : [];
  const lines: string[] = [];
  let malformedTurnCount = 0;

  for (let i = 0; i < raw.length; i += 1) {
    const parsed = enrichedTurnSchema.safeParse(raw[i]);
    if (!parsed.success) {
      malformedTurnCount += 1;
      continue;
    }
    const turn = parsed.data;
    const text = turn.content
      .filter((b: ContentBlock): b is Extract<ContentBlock, { type: 'text' }> => b.type === 'text')
      .map((b) => b.text)
      .join(' ')
      .trim();
    if (text) lines.push(`[${i}] ${turn.role}: ${text}`);
  }

  const { kept, shortenedTurn } = boundToRecentLines(lines, maxBytes);
  return {
    transcript: kept.join('\n'),
    turnCount: kept.length,
    malformedTurnCount,
    // Turns DROPPED by the cap, plus the one turn that was shortened in place
    // when a single oversized turn had to be kept — dropped and shortened are
    // both lost context, and content never leaves the input silently.
    truncatedTurnCount: lines.length - kept.length + (shortenedTurn ? 1 : 0),
  };
}

/** The last `maxBytes` worth of whole characters, never splitting one in half. */
function keepTailBytes(text: string, maxBytes: number): string {
  // Code-point split without iteration syntax or a `u`-flag literal: this
  // package is also compiled under consumers whose TS target predates ES2015,
  // where string spread and unicode regex literals are rejected at compile
  // time. The constructor form defers the flag to runtime, where it is
  // universally supported.
  const chars = text.match(new RegExp('[\\s\\S]', 'gu')) ?? [];
  let total = 0;
  let start = chars.length;
  for (let i = chars.length - 1; i >= 0; i -= 1) {
    const char = chars[i];
    if (char === undefined) continue;
    const cost = Buffer.byteLength(char, 'utf-8');
    if (total + cost > maxBytes) break;
    total += cost;
    start = i;
  }
  return chars.slice(start).join('');
}

/**
 * Shorten one projected line to the ceiling, keeping its tail behind the
 * `[i] role: ` prefix — what matters in an outsized turn is voiced late in it,
 * and losing the prefix would break the line's citation coordinate.
 */
function truncateLineToBytes(line: string, maxBytes: number): string {
  if (Buffer.byteLength(line, 'utf-8') <= maxBytes) return line;
  const sep = line.indexOf(': ');
  const prefix = sep > 0 ? line.slice(0, sep + 2) : '';
  const budget = maxBytes - Buffer.byteLength(prefix, 'utf-8');
  if (budget <= 0) return keepTailBytes(line, maxBytes);
  return prefix + keepTailBytes(line.slice(prefix.length), budget);
}

/**
 * Keep the newest lines that fit the byte cap, measured on the exact string
 * the model receives — the joining '\n' separators are part of the budget. At
 * least one line always survives (an empty projection would read downstream as
 * "no transcript"), shortened to the ceiling rather than forwarded whole.
 */
function boundToRecentLines(
  lines: string[],
  maxBytes: number,
): { kept: string[]; shortenedTurn: boolean } {
  let total = 0;
  const kept: string[] = [];
  for (let i = lines.length - 1; i >= 0; i -= 1) {
    const line = lines[i];
    if (line === undefined) continue;
    const cost = Buffer.byteLength(line, 'utf-8') + (kept.length > 0 ? 1 : 0);
    if (kept.length > 0 && total + cost > maxBytes) break;
    kept.unshift(line);
    total += cost;
  }
  const onlyLine = kept.length === 1 ? kept[0] : undefined;
  if (total > maxBytes && onlyLine !== undefined) {
    return { kept: [truncateLineToBytes(onlyLine, maxBytes)], shortenedTurn: true };
  }
  return { kept, shortenedTurn: false };
}
